import { Button } from "@do/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@do/ui/components/tooltip";
import { cn } from "@do/ui/lib/utils";
import { PencilIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect } from "react";
import { useChat } from "../../../../providers/chat-provider";

interface MessageActionEditButtonProps {
  id: string;
}

export const MessageActionEditButton = ({ id }: MessageActionEditButtonProps) => {
  const { onEditBotResponse, isTestingMode, disabled } = useChat();
  const t = useTranslations("do-chat-v2");

  const removeHighlight = useCallback(() => {
    const elemsHighlighted = Array.from(document.getElementsByClassName("highlight"));
    elemsHighlighted.forEach((elem) => {
      elem.classList.remove("highlight");
    });
  }, []);

  const onClickOutside = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      // disable highlight if click happens outside a highlighted zone
      const targetClassName = (e?.target as Element)?.className;
      if (!targetClassName?.includes?.("highlight")) removeHighlight();
      onEditBotResponse?.("", false);
    },
    [onEditBotResponse, removeHighlight]
  );

  useEffect(() => {
    if (!isTestingMode) return;
    document.addEventListener("click", onClickOutside);
    return () => {
      document.removeEventListener("click", onClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestingMode]);

  const highlightMessageTextBlocks = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!id || !onEditBotResponse) return;
      e.stopPropagation();

      removeHighlight();

      const messageElem = document.getElementById(id);
      if (!messageElem) return;

      const textBlocks = Array.from(
        messageElem.querySelectorAll<HTMLElement>(".chat-message-text")
      );
      textBlocks.forEach((elem) => elem.classList.add("highlight"));

      onEditBotResponse(id, true);
    },
    [id, onEditBotResponse, removeHighlight]
  );

  return (
    <Tooltip group>
      <TooltipTrigger asChild>
        <Button
          id={`message-${id}-edit-button`}
          onClick={highlightMessageTextBlocks}
          disabled={disabled}
          size="icon"
          className={cn(
            "size-fit",
            "rounded-(--message-actions-border-radius)",
            "p-(--message-actions-padding)",
            "bg-(--primary-buttons-background-color)/(--primary-buttons-background-opacity)",
            "hover:bg-(--primary-buttons-hover-background-color)/(--primary-buttons-hover-background-opacity)",
            "text-(--primary-buttons-foreground-color)/(--primary-buttons-foreground-opacity)",
            "hover:text-(--primary-buttons-hover-foreground-color)/(--primary-buttons-hover-foreground-opacity)"
          )}
          aria-label={t("chat.messages.accessibility.message-edit-aria-label")}
        >
          <PencilIcon
            id={`message-${id}-edit-button-icon`}
            aria-hidden="true"
            className="size-(--message-actions-icon-size)"
          />
        </Button>
      </TooltipTrigger>

      <TooltipContent
        id={`message-${id}-edit-button-tooltip`}
        side="bottom"
        arrowClassName={cn(
          "bg-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)",
          "fill-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)"
        )}
        className={cn(
          "bg-(--message-actions-tooltip-background)/(--message-actions-tooltip-background-opacity)",
          "text-(--message-actions-tooltip-foreground)/(--message-actions-tooltip-foreground-opacity)"
        )}
      >
        {t("chat.messages.actions.edit")}
      </TooltipContent>
    </Tooltip>
  );
};
